import { useQueryClient } from "@tanstack/react-query";
import { invalidateSocialGraph } from "Api/Mutations/Social/invalidateSocialGraph";
import { signalRService } from "Infrastructure/SignalR/SignalRService";
import { useEffect } from "react";
import { useSelector } from "react-redux";
import type { RootStateType } from "State/Store";
import { isAuthenticated } from "Utils/AuthUtils";

/**
 * Keeps the PASSIVE side of a social-graph change fresh. The person who follows, approves,
 * unfollows or blocks invalidates their own caches in the mutation; the other person's caches
 * (disclosed names in chat, the relationship buttons, follow lists, the request badge) would
 * otherwise wait for the query staleness plus a focus or reload. The backend pushes an opaque
 * "changed" message to them over the notifications hub, and this hook refetches the graph's
 * read families — never the chat token (invalidateChatTokenForMutualFollow), whose fetch writes
 * the Stream profile; the pair's DM was already promoted by the command that completed it. No
 * polling, no Stream traffic.
 *
 * Mounted once at app level, next to useSignalRNotifications, which owns the connection. It
 * subscribes through the ready callback (immediate when already connected, queued otherwise), and
 * re-runs on every sign-in: a logout tears the connection down with its handlers, and the next
 * sign-in builds a new one.
 */
export const useSocialGraphSignalR = () => {
  const queryClient = useQueryClient();
  const user = useSelector((state: RootStateType) => state.auth.user);
  const isUserAuthenticated = isAuthenticated(user);

  useEffect(() => {
    if (!isUserAuthenticated) {
      return;
    }

    const handleChange = () => invalidateSocialGraph(queryClient);

    const cleanupConnectionReady = signalRService.onConnectionReady(() => {
      signalRService.onReceiveSocialGraphChange(handleChange);
    });

    return () => {
      signalRService.offReceiveSocialGraphChange(handleChange);
      cleanupConnectionReady();
    };
  }, [queryClient, isUserAuthenticated]);
};
